import { useCallback } from 'react';

import { ERROR_CODE, ERROR_TEXT } from '@packages/constants';
import { RegionState, isUSRegionSelector, useConfigStore, useRegionStore } from '@packages/stores';
import { Region } from '@packages/types';

import { Assets } from '@assets';
import { ErrorScreenProps } from '@components';
import { i18n } from '@i18n';

type Props = {
  quoteId?: string | null;
  region?: Region;
  onGetNewQuote: () => void;
  onGoHomepage: () => void;
};

function assistancePhoneNumberSelector(state: RegionState) {
  const isUS = isUSRegionSelector(state);
  return isUS
    ? 'errors.errorBoundary.phoneNumber.maintenanceMode'
    : 'errors.errorBoundary.phoneNumber.quoteNotFound';
}

export const useErrorText = ({ quoteId, onGetNewQuote, onGoHomepage }: Props) => {
  const phoneNumberI18NText = useRegionStore(assistancePhoneNumberSelector);

  const assistanceNumberConfig =
    useConfigStore(state => state.regionSpecificConfig?.CUSTOMER_SERVICE_OVERSEAS_NUMBER) ??
    '+61 2 8907 5085';

  const getErrorScreenProps = useCallback(
    (err?: Error): ErrorScreenProps | null => {
      const assistanceNumber = i18n.t(phoneNumberI18NText);
      const errorText =
        ERROR_TEXT[err?.name as keyof typeof ERROR_TEXT] ?? ERROR_TEXT[ERROR_CODE.INTERNAL_ERROR];

      const errorProps: ErrorScreenProps = {
        titleTextProps: {
          children: i18n.t(errorText.titleI18Text),
          variant: 'h3-28/sb',
        },
        bodyTextProps: {
          children: i18n.t(errorText.bodyI18Text, { QUOTE_ID: quoteId ?? '' }),
          variant: 'body-16/m',
        },
        Icon: Assets.FaceFrown,
        actions: [
          {
            title: i18n.t('errors.errorBoundary.buttonActions.getANewQuote'),
            variant: 'secondary',
            onClick: onGetNewQuote,
          },
        ],
      };

      switch (err?.name) {
        case ERROR_CODE.QUOTE_ALREADY_PAID:
          errorProps.Icon = Assets.CheckCircleOutline;
          break;

        case ERROR_CODE.QUOTE_NOT_FOUND:
          errorProps.actions = [
            {
              title: i18n.t('errors.errorBoundary.buttonActions.getANewQuote'),
              variant: 'secondary',
              onClick: onGetNewQuote,
            },
            {
              title: i18n.t(phoneNumberI18NText),
              variant: 'primary',
              onClick: () => {
                window.location.href = `tel:${assistanceNumber?.replace(/\s+/g, '')}`;
              },
            },
          ];
          break;
        case ERROR_CODE.QUOTE_INVALID_STATUS:
          errorProps.actions = [
            {
              title: i18n.t('errors.errorBoundary.buttonActions.getANewQuote'),
              variant: 'secondary',
              onClick: onGetNewQuote,
            },
            {
              title: i18n.t(phoneNumberI18NText),
              variant: 'primary',
              onClick: () => {
                window.location.href = `tel:${assistanceNumber?.replace(/\s+/g, '')}`;
              },
            },
          ];
          break;

        case ERROR_CODE.QUOTE_START_DATE_PASSED:
          errorProps.actions = [
            {
              title: i18n.t('errors.errorBoundary.buttonActions.getANewQuote'),
              variant: 'secondary',
              onClick: onGetNewQuote,
            },
          ];
          break;

        case ERROR_CODE.QUOTE_END_DATE_PASSED:
          errorProps.actions = [
            {
              title: i18n.t('errors.errorBoundary.buttonActions.getANewQuote'),
              variant: 'secondary',
              onClick: onGetNewQuote,
            },
          ];
          break;

        case ERROR_CODE.PAYMENT_IN_PROGRESS:
          errorProps.actions = [
            {
              title: assistanceNumberConfig,
              variant: 'secondary',
              onClick: () => {
                window.location.href = `tel:${assistanceNumberConfig?.replace(/\s+/g, '')}`;
              },
            },
          ];
          break;

        case ERROR_CODE.INTERNAL_ERROR:
          errorProps.actions = [
            {
              title: i18n.t('errors.errorBoundary.buttonActions.backToHomePage'),
              variant: 'secondary',
              onClick: onGoHomepage,
            },
          ];
          break;
      }

      return errorProps;
    },
    [quoteId, onGetNewQuote, phoneNumberI18NText, assistanceNumberConfig, onGoHomepage],
  );

  return {
    getErrorScreenProps,
  };
};
